import {
  Badge,
  Box,
  Divider,
  HStack,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react'
import type { TFunction } from 'i18next'
import type { OrderLifecycleDeployment, OrderLifecycleSweepExtend } from '@/types'

interface OrderDeploymentsSectionProps {
  deployments?: OrderLifecycleDeployment[]
  sweepExtend?: OrderLifecycleSweepExtend | null
  paymentAddress?: string | null
  renderTxCell: (value: string | null | undefined, chainId?: string | number | null) => JSX.Element | null
  renderAddressCell: (value?: string | null) => JSX.Element
  formatStatus: (status: string | undefined, translate: TFunction) => string
  formatTimestamp: (value?: string | null, locale?: string) => string | null
  renderKeyValue: (label: string, value?: string | null) => JSX.Element | null
  t: TFunction
  locale?: string
  chainId?: string | number | null
  chainCompatibility?: string | null
}

export const OrderDeploymentsSection = ({
  deployments,
  sweepExtend,
  paymentAddress,
  renderTxCell,
  renderAddressCell,
  formatStatus,
  formatTimestamp,
  renderKeyValue,
  t,
  locale,
  chainId,
  chainCompatibility,
}: OrderDeploymentsSectionProps) => {
  if (!deployments || deployments.length === 0) {
    return <Text fontSize="sm">{t('orders.lifecycle.noData')}</Text>
  }

  const isSolana = chainCompatibility?.trim().toLowerCase() === 'solana'
  const recordsLabel = t('orders.lifecycle.processingRecords', 'Processing Records')
  const recordLabel = t('orders.lifecycle.processingRecord', 'Record')
  const txLabel = t('orders.lifecycle.txHash')
  const blockLabel = t('orders.lifecycle.blockOrSlot', 'Block / Slot')

  const renderSweepExtendSummary = (extend: OrderLifecycleSweepExtend | null | undefined) => {
    if (!extend) {
      return null
    }

    const notes = Array.isArray(extend.sweepExtendNotes) ? extend.sweepExtendNotes : []
    const rawTotalExtensions =
      typeof extend.totalExtensions === 'number' ? extend.totalExtensions : 0
    const totalExtensions = Math.max(rawTotalExtensions, notes.length)

    const formatOrNA = (value?: string | null) =>
      formatTimestamp(value, locale) ?? (value ?? t('common.na'))

    const hasActivity = notes.length > 0 || (totalExtensions ?? 0) > 0

    return (
      <Box borderWidth="1px" borderRadius="md" p={4}>
        <VStack align="stretch" spacing={2}>
          <Text fontSize="sm" fontWeight="medium">
            {t('orders.lifecycle.sweepExtendSummary', 'Sweep extension summary')}
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} fontSize="sm">
            <Text>
              {t('orders.lifecycle.sweepUnlock')}: {formatOrNA(extend.currentUnlockAt)}
            </Text>
            <Text>
              {t('orders.lifecycle.sweepUnlockMax')}: {formatOrNA(extend.sweepExtendMaxAt)}
            </Text>
            <Text>
              {t('orders.lifecycle.sweepExtendCount', 'Total extensions')}: {totalExtensions ?? 0}
            </Text>
          </SimpleGrid>
          {hasActivity ? (
            <VStack align="stretch" spacing={3} mt={2}>
              {extend.sweepExtendNotes?.[0]?.note && (
                <Text fontSize="sm" color="gray.600">
                  {extend.sweepExtendNotes[0].note}
                </Text>
              )}
              {notes.map((note, index) => {
                const requestedAt = formatOrNA(note.requestedAt)
                const requestedUnlock = formatOrNA(note.requestedUnlockAt)
                const resultingUnlock = note.resultingUnlockAt
                  ? formatOrNA(note.resultingUnlockAt)
                  : undefined
                const executedAt = note.executedAt ? formatOrNA(note.executedAt) : undefined
                const extendDuration =
                  typeof note.extendSeconds === 'number'
                    ? `${Math.max(0, Math.floor(note.extendSeconds / 3600))}h`
                    : t('common.na')

                return (
                  <Box key={`${note.requestedAt ?? 'note'}-${index}`} borderWidth="1px" borderRadius="md" p={3}>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2} fontSize="sm">
                      <Text>
                        {t('orders.lifecycle.requestedAt')}: {requestedAt}
                      </Text>
                      <Text>
                        {t('orders.lifecycle.requestedUnlockAt')}: {requestedUnlock}
                      </Text>
                      <Text>
                        {t('orders.lifecycle.resultingUnlockAt')}: {resultingUnlock ?? t('common.na')}
                      </Text>
                      <Text>
                        {t('orders.lifecycle.executedAt')}: {executedAt ?? t('common.na')}
                      </Text>
                      <Text>
                        {t('orders.lifecycle.extendDuration')}: {extendDuration}
                      </Text>
                    </SimpleGrid>
                    {note.note && (
                      <Text fontSize="sm" mt={2}>
                        {note.note}
                      </Text>
                    )}
                  </Box>
                )
              })}
            </VStack>
          ) : (
            <Text fontSize="sm" color="gray.500">
              {t('orders.lifecycle.noData')}
            </Text>
          )}
        </VStack>
      </Box>
    )
  }

  return (
    <VStack align="stretch" spacing={4}>
      <Text fontSize="sm" fontWeight="medium">
        {recordsLabel}
      </Text>
      {deployments.map(deployment => (
        <Box key={deployment.id} borderWidth="1px" borderRadius="md" p={4}>
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text fontWeight="medium">
                {recordLabel} #{deployment.attempt}
              </Text>
              <Badge>{formatStatus(deployment.status, t)}</Badge>
            </HStack>
            {deployment.tx?.hash && (
              <HStack spacing={3} align="flex-start">
                <Text fontSize="sm" fontWeight="medium">
                  {txLabel}:
                </Text>
                {renderTxCell(deployment.tx.hash, chainId)}
              </HStack>
            )}
            <VStack align="stretch" spacing={2}>
              {isSolana ? (
                <>
                  <HStack spacing={3} align="flex-start">
                    <Text fontSize="sm" fontWeight="medium" minW="140px">
                      {t('orders.lifecycle.paymentAddress', 'Payment Address')}
                    </Text>
                    {renderAddressCell(paymentAddress)}
                  </HStack>
                  <HStack spacing={3} align="flex-start">
                    <Text fontSize="sm" fontWeight="medium" minW="140px">
                      {t('orders.lifecycle.vaultTokenAccount', 'Vault Token Account')}
                    </Text>
                    {renderAddressCell(deployment.contractAddress)}
                  </HStack>
                </>
              ) : (
                <HStack spacing={3} align="flex-start">
                  <Text fontSize="sm" fontWeight="medium" minW="140px">
                    {t('orders.lifecycle.contract', 'Contract Address')}
                  </Text>
                  {renderAddressCell(deployment.contractAddress)}
                </HStack>
              )}
            </VStack>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} fontSize="sm">
              <Text>
                {blockLabel}: {deployment.tx?.blockNumber ?? '-'}
              </Text>
              {!isSolana ? (
                <Text>
                  {t('orders.lifecycle.gasUsed')}: {deployment.tx?.gasUsed ?? '-'}
                </Text>
              ) : null}
              {!isSolana ? (
                <Text>
                  {t('orders.lifecycle.gasPrice')}: {deployment.tx?.gasPrice ?? '-'}
                </Text>
              ) : null}
              <Text>
                {t('orders.lifecycle.costUsd')}: {deployment.tx?.costUsd ?? '-'}
              </Text>
            </SimpleGrid>
            <Divider />
            <VStack align="stretch" spacing={2}>
              <Text fontSize="sm" fontWeight="medium">
                {t('orders.lifecycle.verification')}
              </Text>
              {renderKeyValue(
                t('orders.lifecycle.status'),
                formatStatus(deployment.verification?.status, t)
              )}
              {renderKeyValue(
                t('orders.lifecycle.notes'),
                deployment.verification?.notes ?? undefined
              )}
              {renderKeyValue(
                t('orders.lifecycle.sweepUnlock'),
                formatTimestamp(deployment.verification?.sweepOpenAt, locale) ?? undefined
              )}
              {deployment.verification?.isInitialized !== undefined && (
                <Text fontSize="sm">
                  {t('orders.lifecycle.initialized')}:{' '}
                  {deployment.verification.isInitialized ? t('common.yes') : t('common.no')}
                </Text>
              )}
              {renderKeyValue(
                t('orders.lifecycle.initializationNotes'),
                deployment.verification?.initializationNotes ?? undefined
              )}
            </VStack>
          </VStack>
        </Box>
      ))}
      {renderSweepExtendSummary(sweepExtend)}
    </VStack>
  )
}
